import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { ArrowLeft, Eye, EyeOff, Lock } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";

import type { ApiError } from "@/types/auth.type";
import { CLINICAL_FONT_FAMILY } from "@/components/shared/constants";
import { container } from "@/container/DependencyContainer";
import {
  formatPasswordComplexitySummary,
  getPasswordComplexitySummaryErrors,
} from "@/utils/passwordValidation";

interface ChangeExpiredPasswordPageProps {
  onBackToLogin?: () => void;
}

const ChangeExpiredPasswordPage: React.FC<ChangeExpiredPasswordPageProps> = ({
  onBackToLogin,
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get("email") ?? "");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const passwordValidation = useMemo(() => {
    if (!newPassword) {
      return { isValid: false, errors: [] as string[] };
    }

    const errors = getPasswordComplexitySummaryErrors(newPassword);
    return { isValid: errors.length === 0, errors };
  }, [newPassword]);

  const passwordsMatch =
    confirmPassword.length > 0 && confirmPassword === newPassword;

  const handleBack = () => {
    if (onBackToLogin) {
      onBackToLogin();
      return;
    }
    navigate("/login");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!passwordValidation.isValid) {
      setError(
        formatPasswordComplexitySummary(newPassword) ||
          "Please ensure your new password meets all requirements.",
      );
      setSnackbarOpen(true);
      setLoading(false);
      return;
    }

    if (!passwordsMatch) {
      setError("New password and confirmation do not match.");
      setSnackbarOpen(true);
      setLoading(false);
      return;
    }

    try {
      const result = await container.changeExpiredPasswordUseCase.execute({
        email,
        currentPassword,
        newPassword,
      });
      setSuccessMessage(result.message);
      setTimeout(() => navigate("/login"), 2000);
    } catch (apiError) {
      setError(
        (apiError as ApiError)?.message ||
          "Failed to change password. Please try again.",
      );
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #F8FAFC 0%, #EEF2FF 100%)",
        fontFamily: CLINICAL_FONT_FAMILY,
        p: 2,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, sm: 4 },
            borderRadius: 3,
            fontFamily: CLINICAL_FONT_FAMILY,
          }}
        >
          <Button
            startIcon={<ArrowLeft size={18} />}
            onClick={handleBack}
            sx={{ mb: 2, textTransform: "none" }}
          >
            Back to login
          </Button>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
            <Lock size={28} color="#0B5FFF" />
            <Typography variant="h5" fontWeight={700}>
              Change Expired Password
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Your password has expired. Enter your current password and choose a
            new one to continue.
          </Typography>

          {successMessage ? (
            <Alert severity="success">{successMessage}</Alert>
          ) : (
            <Box
              component="form"
              onSubmit={(e) => {
                e.preventDefault();
                void handleSubmit(e);
              }}
            >
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                margin="normal"
                autoComplete="email"
              />

              <TextField
                fullWidth
                label="Current Password"
                type={showCurrentPassword ? "text" : "password"}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                margin="normal"
                autoComplete="current-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          setShowCurrentPassword(!showCurrentPassword)
                        }
                        edge="end"
                      >
                        {showCurrentPassword ? (
                          <EyeOff size={18} />
                        ) : (
                          <Eye size={18} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="New Password"
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                margin="normal"
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => {
                          void setShowNewPassword(!showNewPassword);
                        }}
                        edge="end"
                      >
                        {showNewPassword ? (
                          <EyeOff size={18} />
                        ) : (
                          <Eye size={18} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {newPassword && !passwordValidation.isValid && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  {formatPasswordComplexitySummary(newPassword)}
                </Alert>
              )}

              <TextField
                fullWidth
                label="Confirm New Password"
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                margin="normal"
                autoComplete="new-password"
                error={
                  confirmPassword.length > 0 && confirmPassword !== newPassword
                }
                helperText={
                  confirmPassword.length > 0 && confirmPassword !== newPassword
                    ? "Passwords do not match"
                    : " "
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        edge="end"
                      >
                        {showConfirmPassword ? (
                          <EyeOff size={18} />
                        ) : (
                          <Eye size={18} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{
                  mt: 3,
                  py: 1.4,
                  textTransform: "none",
                  fontWeight: 600,
                  background:
                    "linear-gradient(135deg, #0B5FFF 0%, #0FB6A6 100%)",
                }}
              >
                {loading ? (
                  <CircularProgress size={22} color="inherit" />
                ) : (
                  "Update Password"
                )}
              </Button>
            </Box>
          )}
        </Paper>
      </Container>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="error"
          variant="filled"
          sx={{ fontFamily: CLINICAL_FONT_FAMILY, minWidth: 300 }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ChangeExpiredPasswordPage;
